import { Controller } from "@hotwired/stimulus"

// 사업자 등록번호 형식 검증 및 중복 확인 컨트롤러
// - 입력 시 형식 검증
// - 중복확인 버튼 클릭 시 API 호출하여 중복 확인
export default class extends Controller {
  static targets = [
    "input",
    "checkButton",
    "validMessage",
    "invalidMessage",
    "availableMessage",
    "duplicateMessage",
    "loadingSpinner"
  ]

  static outlets = ["company-form"]

  connect() {
    console.log('[BN] Controller connected')
    this.hideAllMessages()
    this.isChecking = false
    this.isAvailable = false
  }

  // 입력 시 중복확인 상태 초기화
  onInput() {
    this.isAvailable = false
    this.hideAllMessages()
    this.resetInputStyle()
    this.enableCheckButton()

    // company-form에 상태 전달
    this.notifyCompanyForm()
  }

  // blur 시 자동 포맷팅 및 형식 검증
  validate() {
    console.log('[BN] validate() called')

    // 이미 중복확인 완료된 경우 검증 메시지 표시하지 않음
    if (this.isAvailable) {
      console.log('[BN] Already available, skipping')
      return
    }

    let value = this.inputTarget.value.trim()
    console.log('[BN] Current value:', value, 'includes dash:', value.includes('-'))

    // '-'가 없으면 자동으로 포맷팅
    if (value.length > 0 && !value.includes('-')) {
      const formatted = this.formatBusinessNumber(value)
      console.log('[BN] Formatted:', formatted)
      value = formatted
      this.inputTarget.value = value
    }

    console.log('[BN] isValidFormat:', this.isValidFormat(value))

    if (value && this.isValidFormat(value)) {
      this.showValidFormat()
    } else if (value) {
      this.showInvalidFormat()
    }
  }

  // 사업자 등록번호 자동 포맷팅 (숫자만 있을 때 '-' 삽입)
  formatBusinessNumber(value) {
    // 숫자만 추출
    const digits = value.replace(/[^0-9]/g, '')

    // 10자리면 XXX-XX-XXXXX 형식으로 포맷팅
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
    }

    // 포맷팅할 수 없으면 원본 반환
    return value
  }

  // 중복 확인 버튼 클릭
  async checkDuplicate() {
    const businessNumber = this.inputTarget.value.trim()

    if (!businessNumber) {
      return
    }

    if (!this.isValidFormat(businessNumber)) {
      this.showInvalidFormat()
      return
    }

    if (this.isChecking) {
      return
    }

    this.isChecking = true
    this.showLoading()

    try {
      const response = await fetch('/company/registration/check_business_number', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify({ business_number: businessNumber })
      })

      const data = await response.json()

      if (data.success && data.available) {
        this.isAvailable = true
        this.showAvailable()
        this.showResultAlert('사용 가능한 사업자 등록번호입니다.')
      } else {
        this.isAvailable = false
        const msg = data.error?.message || '이미 등록된 사업자 등록번호입니다.'
        this.markBusinessNumberUnavailable()
        this.showResultAlert(msg)
      }
    } catch (error) {
      console.error('사업자 등록번호 중복 확인 오류:', error)
      this.isAvailable = false
      const errMsg = '중복 확인 중 오류가 발생했습니다.'
      this.markBusinessNumberUnavailable()
      this.showResultAlert(errMsg)
    } finally {
      this.isChecking = false
      this.hideLoading()
      this.notifyCompanyForm()
    }
  }

  // CSRF 토큰 가져오기
  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  // 사업자 등록번호 형식 검증 (XXX-XX-XXXXX)
  isValidFormat(value) {
    return /^\d{3}-\d{2}-\d{5}$/.test(value)
  }

  hideAllMessages() {
    if (this.hasValidMessageTarget) {
      this.validMessageTarget.classList.add("hidden")
      this.validMessageTarget.style.display = "none"
    }
    if (this.hasInvalidMessageTarget) {
      this.invalidMessageTarget.classList.add("hidden")
      this.invalidMessageTarget.style.display = "none"
    }
    if (this.hasAvailableMessageTarget) {
      this.availableMessageTarget.classList.add("hidden")
      this.availableMessageTarget.style.display = "none"
    }
    if (this.hasDuplicateMessageTarget) {
      this.duplicateMessageTarget.classList.add("hidden")
      this.duplicateMessageTarget.style.display = "none"
    }
  }

  resetInputStyle() {
    this.inputTarget.classList.remove("border-[var(--success-500)]", "border-[var(--danger-500)]")
  }

  showValidFormat() {
    this.hideAllMessages()
    if (this.hasValidMessageTarget) {
      this.validMessageTarget.classList.remove("hidden")
      this.validMessageTarget.style.display = "flex"
    }
  }

  showInvalidFormat() {
    this.hideAllMessages()
    if (this.hasInvalidMessageTarget) {
      this.invalidMessageTarget.classList.remove("hidden")
      this.invalidMessageTarget.style.display = "block"
    }
    this.inputTarget.classList.remove("border-[var(--success-500)]")
    this.inputTarget.classList.add("border-[var(--danger-500)]")
  }

  showAvailable() {
    this.hideAllMessages()
    if (this.hasAvailableMessageTarget) {
      this.availableMessageTarget.classList.remove("hidden")
      this.availableMessageTarget.style.display = "flex"
    }
    this.inputTarget.classList.remove("border-[var(--danger-500)]")
    this.inputTarget.classList.add("border-[var(--success-500)]")
    this.disableCheckButton()
  }

  // 인라인 중복 메시지는 alert 로만 안내하므로 텍스트는 노출하지 않음 —
  // input 의 border 상태와 다른 인라인 메시지 hide 만 적용.
  markBusinessNumberUnavailable() {
    this.hideAllMessages()
    this.inputTarget.classList.remove("border-[var(--success-500)]")
    this.inputTarget.classList.add("border-[var(--danger-500)]")
  }

  // components/basics/alert 컴포넌트를 dispatch 이벤트로 열기.
  showResultAlert(message) {
    const alertEl = document.getElementById('business-number-check-result-alert')
    if (!alertEl) return
    const msgEl = alertEl.querySelector("[data-alert-target='body'] p")
    if (msgEl) msgEl.textContent = message
    alertEl.dispatchEvent(new CustomEvent('alert:open'))
  }

  showLoading() {
    if (this.hasCheckButtonTarget) this.checkButtonTarget.disabled = true
    if (this.hasLoadingSpinnerTarget) this.loadingSpinnerTarget.classList.remove("hidden")
    const buttonText = this.checkButtonTarget?.querySelector('.button-text')
    if (buttonText) buttonText.classList.add("opacity-0")
  }

  hideLoading() {
    if (!this.isAvailable && this.hasCheckButtonTarget) {
      this.checkButtonTarget.disabled = false
    }
    if (this.hasLoadingSpinnerTarget) this.loadingSpinnerTarget.classList.add("hidden")
    const buttonText = this.checkButtonTarget?.querySelector('.button-text')
    if (buttonText) buttonText.classList.remove("opacity-0")
  }

  enableCheckButton() {
    if (this.hasCheckButtonTarget) this.checkButtonTarget.disabled = false
  }

  disableCheckButton() {
    if (this.hasCheckButtonTarget) this.checkButtonTarget.disabled = true
  }

  // company-form 컨트롤러에 상태 알림
  notifyCompanyForm() {
    if (this.hasCompanyFormOutlet) {
      this.companyFormOutlet.updateBusinessNumberStatus(this.isAvailable)
    }
  }

  // 외부에서 중복확인 상태 확인용
  get isBusinessNumberAvailable() {
    return this.isAvailable
  }
}
